import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockKeyhole, LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordStrength, getPasswordStrength } from '@/components/PasswordStrength';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('');
  const strength = getPasswordStrength(newPassword);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!data) navigate('/'); else setUserRole(data.role || ''); })
      .catch(() => navigate('/'));
  }, [navigate]);

  const handleLogout = async () => {
    try { await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch (err) { console.warn('Falha ao sair', err); }
    navigate('/');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!strength.valid) {
      setError('A nova senha precisa cumprir todos os requisitos.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('A confirmação não confere com a nova senha.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Não foi possível alterar a senha.');
      toast.success('Senha alterada com sucesso');
      const me = await fetch(`${API}/auth/me`, { credentials: 'include' }).then(r => r.json());
      navigate(me.role === 'gestor' ? '/gestor' : me.role === 'admin' ? '/admin' : me.role === 'ebi_coordenadora' ? '/ebi' : me.role === 'movimentos_coordenador' ? '/movimentos-painel' : '/auxiliar');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#dbeafe_0,#f8fafc_34%,#eef2ff_100%)] flex items-center justify-center px-4 py-10">
      <form onSubmit={handleSubmit} className="relative w-full max-w-md rounded-2xl border border-white/70 bg-white/90 p-6 shadow-2xl shadow-slate-200/80 space-y-5" data-testid="change-password-form">
        <Button type="button" variant="ghost" size="icon" onClick={handleLogout} data-testid="change-password-logout-button" className="absolute right-3 top-3 text-slate-500 hover:text-red-600" title="Sair">
          <LogOut className="h-4 w-4" />
        </Button>
        <div className="space-y-2">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Defina sua senha</h1>
          <p className="text-sm text-slate-600">Este acesso usa uma senha temporária. Crie uma senha forte para continuar.</p>
        </div>
        {error && <Alert variant="destructive" data-testid="change-password-error"><AlertDescription>{error}</AlertDescription></Alert>}
        <input type="hidden" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="change-password-current-input" />
        {userRole && <p className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="change-password-temporary-note">Você já confirmou a senha temporária no login. Defina apenas a nova senha.</p>}
        <div className="space-y-2">
          <Label htmlFor="new-password">Nova senha</Label>
          <Input id="new-password" data-testid="change-password-new-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
          <PasswordStrength password={newPassword} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirmar nova senha</Label>
          <Input id="confirm-password" data-testid="change-password-confirm-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
        </div>
        <Button type="submit" data-testid="change-password-submit-button" disabled={loading || !strength.valid} className="w-full gap-2 rounded-xl bg-blue-600 hover:bg-blue-700">
          <LockKeyhole className="h-4 w-4" />
          {loading ? 'Salvando...' : 'Salvar nova senha'}
        </Button>
      </form>
    </div>
  );
}